<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useRuleSetStore } from '@/stores/ruleSet';
import { useProductConfigurationStore } from '@/stores/productConfiguration';
import RuleAttributeRenderer from '@/components/RuleEngine/RuleAttributeRenderer.vue';
import { useRuleAttributeErrors } from '@/composables/ruleEngine/useRuleAttributeErrors';

const page = usePage();

const ruleSetStore = useRuleSetStore();
const productConfigStore = useProductConfigurationStore();

const { form: ruleSetForm, detail: ruleSetDetail } = storeToRefs(ruleSetStore);
const { detail: productConfigDetail } = storeToRefs(productConfigStore);

const saving = ref(false);
const loadingProductDetail = ref(false);

const isClone = computed(() => page.url.endsWith('/clone'));
const isEdit = computed(() => page.url.startsWith('/rule-engine/') && !page.url.endsWith('/create') && !isClone.value);

const pageTitle = computed(() => {
    if (isEdit.value) {
        return 'label.rule_engine';
    }
    if (isClone.value) {
        return 'label.clone_rule_set';
    }
    return 'label.new_rule_set';
});

const products = computed(() => ruleSetForm.value?.products ?? []);
const labels = computed(() => ruleSetForm.value?.labels ?? []);
const detail = computed(() => ruleSetDetail.value ?? null);

const localForm = ref({
    product_configuration_id: null,
    title: '',
    is_active: true,
    label_ids: [],
    rules: [], // [{ product_attribute_id, is_enabled, sort_order, rule_json }]
});

const selectedProduct = computed(() => {
    const id = localForm.value.product_configuration_id;
    return products.value.find(p => p.id === id) ?? null;
});

const detailProductConfiguration = computed(() => detail.value?.rule_set?.product_configuration ?? null);

const normalizeRuleJson = (val) => {
    if (val === null || val === undefined) {
        return null;
    }
    if (typeof val === 'object') {
        return val;
    }
    try {
        return JSON.parse(val);
    } catch (e) {
        return val;
    }
};

const UI_SUPPORTED_ATTRIBUTES = [
    'required_fields',
    'gbc_score',
    'age_limits',
    'age_gap',
    'amount_to_finance',
    'provincial_acceptance',
    'customer_types',
    'tdsr_ratio_value',
    'crp30_score',
    'ltd_value',

    // boolean UI checks
    'cohabitation_check',
    'consumer_statements_check',
    'bankruptcy_indicator_check',
    'shopping_borrowers',
    'material_change',
    'provincial_match',
];

// Provide default rule_json structure based on attribute key
const defaultRuleJsonForKey = (key) => {
    switch (key) {
    case 'gbc_score':
        return {
            min_per_application: null,
            max_per_application: null,
            min_solo_applicant: null,
            max_solo_applicant: null,
        };
    case 'age_limits':
        return {
            applicant_min_age: null,
            applicant_max_age: null,
            co_applicant_min_age: null,
            co_applicant_max_age: null,
        };
    case 'age_gap':
        return { joint_age_gap_limit: null };
    case 'amount_to_finance':
        return { min_amount: null, max_amount: null };
    case 'provincial_acceptance':
        return { allowed_provinces: [] };
    case 'customer_types':
        return { allowed_types: [] };
    case 'tdsr_ratio_value':
        return { max_ratio: null };
    case 'crp30_score':
        return { min_score: null };
    case 'required_fields':
        return { fields: [] };

    // Boolean check types
    case 'cohabitation_check':
    case 'consumer_statements_check':
    case 'bankruptcy_indicator_check':
    case 'shopping_borrowers':
    case 'material_change':
    case 'provincial_match':
    case 'provincial_acceptance_check':
    case 'fraud_watchlist':
    case 'credit_report_age':
    case 'country_code':
    case 'currency_code':
    case 'ltd_value':
    default:
        return { must_be_true: false };
    }
};

// Map rule set detail to form structure
const mapRuleSetToForm = (rs) => ({
    product_configuration_id: rs.product_configuration_id,
    title: isClone.value ? `Copy of ${rs?.title ?? ''}` : (rs?.title ?? ''),
    is_active: !!rs?.is_active,
    label_ids: Array.isArray(rs?.labels) ? rs.labels.map(l => l.id) : [],
    rules: Array.isArray(rs?.rules)
        ? rs.rules.map(r => ({
            product_attribute_id: r.product_attribute_id,
            is_enabled: !!r.is_enabled,
            sort_order: Number(r.sort_order ?? 0),
            rule_json: normalizeRuleJson(r.rule_json ?? null),
        }))
        : [],
});

const hydrateFromDetail = () => {
    if (!isEdit.value && !isClone.value) {
        return;
    }
    const rs = detail.value?.rule_set;
    if (!rs) {
        return;
    }
    localForm.value = mapRuleSetToForm(rs);
};

watch(
    [() => detail.value?.rule_set?.hash, isEdit, isClone],
    hydrateFromDetail,
    { immediate: true }
);

const mappedFieldsForPca = (pca) => {
    const mappings = Array.isArray(pca?.mappings) ? pca.mappings : [];
    const fields = mappings
        .map(m => m?.data_field ?? null)
        .filter(Boolean)
        .map(df => ({
            id: df.id,
            key: df.key,
            name: df.name,
            json_path: df.json_path,
        }));

    // stable ordering
    return fields.sort((a, b) => (a.key ?? '').localeCompare(b.key ?? ''));
};

const rawProductAttributes = computed(() => {
    if (isEdit.value || isClone.value) {
        return detailProductConfiguration.value?.configuration_attributes ?? [];
    }
    return productConfigDetail.value?.configuration_attributes ?? [];
});

const productAttributes = computed(() => {
    const attributes = Array.isArray(rawProductAttributes.value)
        ? rawProductAttributes.value
        : [];

    return attributes
        .filter(attributeConfig => attributeConfig?.is_enabled !== false)
        .filter(attributeConfig =>
            UI_SUPPORTED_ATTRIBUTES.includes(attributeConfig?.attribute?.key ?? '')
        )
        .sort((firstAttribute, secondAttribute) => {
            const firstOrder = Number(firstAttribute?.sort_order ?? 0);
            const secondOrder = Number(secondAttribute?.sort_order ?? 0);

            return firstOrder - secondOrder;
        });
});

const ensureRuleRow = (pca) => {
    const attrId = pca?.product_attribute_id;
    if (!attrId) {
        return null;
    }

    const existingIdx = localForm.value.rules.findIndex(r => r.product_attribute_id === attrId);
    if (existingIdx >= 0) {
        const row = localForm.value.rules[existingIdx];
        if (!row.rule_json || typeof row.rule_json !== 'object') {
            row.rule_json = defaultRuleJsonForKey(pca?.attribute?.key);
        }
        return row;
    }

    const row = {
        product_attribute_id: attrId,
        is_enabled: true,
        sort_order: localForm.value.rules.length + 1,
        rule_json: defaultRuleJsonForKey(pca?.attribute?.key),
    };

    localForm.value.rules.push(row);
    return row;
};

// When a product is selected, build default rule rows for its attributes
const rebuildRulesFromProduct = () => {
    const attrs = productAttributes.value ?? [];
    localForm.value.rules = attrs.map((pca, idx) => ({
        product_attribute_id: pca.product_attribute_id,
        is_enabled: true,
        sort_order: idx + 1,
        rule_json: defaultRuleJsonForKey(pca?.attribute?.key),
    }));
};

const fetchSelectedProductDetail = async () => {
    if (isEdit.value || isClone.value) {
        return;
    }

    const product = selectedProduct.value;
    if (!product?.hash) {
        localForm.value.rules = [];
        return;
    }

    loadingProductDetail.value = true;
    try {
        await productConfigStore.fetchDetail(product.hash);
        rebuildRulesFromProduct();
    } finally {
        loadingProductDetail.value = false;
    }
};

// Refetch product detail when product selection changes
watch(
    () => localForm.value.product_configuration_id,
    async () => {
        await fetchSelectedProductDetail();
    }
);

const buildPayload = () => {
    const rules = (localForm.value.rules ?? []).map(r => ({
        product_attribute_id: r.product_attribute_id,
        is_enabled: !!r.is_enabled,
        sort_order: Number(r.sort_order ?? 0),
        rule_json: (r.rule_json && typeof r.rule_json === 'object') ? r.rule_json : normalizeRuleJson(r.rule_json),
    }));

    return {
        product_configuration_id: localForm.value.product_configuration_id,
        title: localForm.value.title,
        is_active: !!localForm.value.is_active,
        label_ids: Array.isArray(localForm.value.label_ids) ? localForm.value.label_ids : [],
        rules,
    };
};

const goBack = () => router.visit('/rule-engine');

const save = async () => {
    saving.value = true;
    try {
        const payload = buildPayload();

        if (isEdit.value) {
            const rs = detail.value?.rule_set;
            await ruleSetStore.update(rs.hash, payload);
        } else {
            await ruleSetStore.store(payload);
        }

        // Reset cached detail to prevent stale data on subsequent page visits
        ruleSetStore.detail = null;

        goBack();
    } finally {
        saving.value = false;
    }
};

onMounted(async () => {
    if (!products.value.length || !labels.value.length) {
        await ruleSetStore.getForm();
    }

    if (!isEdit.value && !isClone.value && localForm.value.product_configuration_id) {
        await fetchSelectedProductDetail();
    }

    if ((isEdit.value || isClone.value) && productAttributes.value.length && !(localForm.value.rules?.length)) {
        rebuildRulesFromProduct();
    }
});

const errorStore = inject('errorStore');

const { errorsForPca, onAttributeTouched } = useRuleAttributeErrors({
    errorStore,
    localForm,
});

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>

    <template #title>{{ $t(pageTitle) }}</template>

    <template #actions>
        <StyledButton @click="goBack">
            {{ $t('label.cancel') }}
        </StyledButton>
    </template>

    <form class="grid gap-4" @submit.prevent="save">
        <StyledInput
            v-model="localForm.title"
            name="title"
            :label="$t('label.rule_set_title')" />
            
        <StyledDropdown
            v-model="localForm.label_ids"
            name="label_ids"
            :label="$t('label.label')"
            multiple
            :options="labels"
            item-title="name"
            item-value="id"
            clearable />

        <StyledDropdown
            v-model="localForm.product_configuration_id"
            name="product_configuration_id"
            :label="$t('label.product_configuration')"
            :options="products"
            item-title="title"
            item-value="id"
            clearable
            :disabled="isEdit || isClone" />

        <StyledDropdown
            v-model="localForm.is_active"
            name="is_active"
            :label="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]" />

        <div class="mt-2">
            <div class="flex items-center justify-between mb-2">
                <div>
                    <p class="font-semibold mb-2">{{ $t('label.product_attributes') }}</p>
                </div>

                <span v-if="loadingProductDetail" class="text-sm opacity-70">
                    {{ $t('label.loading') }}
                </span>
            </div>

            <div v-if="productAttributes.length" class="grid gap-6">
                <div
                    v-for="pca in productAttributes"
                    :key="pca.id"
                    class="border rounded p-4 bg-white">
                    <div class="flex items-start justify-between gap-3 mb-4">
                        <div>
                            <p class="text-lg font-semibold">
                                {{ pca.attribute?.name ?? '' }}
                            </p>
                        </div>
                    </div>

                    <RuleAttributeRenderer
                        v-if="ensureRuleRow(pca)"
                        :attribute-key="pca.attribute?.key"
                        :mapped-fields="mappedFieldsForPca(pca)"
                        :model-value="ensureRuleRow(pca).rule_json"
                        :errors="errorsForPca(pca)"
                        @touched="() => onAttributeTouched(pca)"
                        @update:modelValue="val => { ensureRuleRow(pca).rule_json = val; }" />
                </div>
            </div>

            <div v-else class="text-sm opacity-70 border rounded p-4">
                {{ $t('label.rule_engine_select_product_hint') }}
            </div>
        </div>

        <div class="flex justify-end gap-2 mt-2">
            <StyledButton variant="secondary" type="button" @click="goBack">
                {{ $t('label.cancel') }}
            </StyledButton>

            <StyledButton type="button" :loading="saving" @click="save">
                {{ $t('label.save') }}
            </StyledButton>
        </div>
    </form>
</MainContentLayout>
</template>
